import * as THREE from 'three';
import { Line2 } from 'three/examples/jsm/lines/Line2.js';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import debug from 'debug';

const log = debug('app:selection-highlight');

interface HighlightConfig {
  color: THREE.Color;
  opacity: number;
  lineWidth: number;
}

interface SelectionEvent {
  selectedObjects: THREE.Object3D[];
}

/**
 * Selection Highlight Manager - Line2-based Approach
 * 
 * Uses Line2 + EdgesGeometry for thick, visible outline rendering.
 * Provides clean outline highlighting that works consistently everywhere.
 * 
 * Features:
 * - Clean edge-based outlines using EdgesGeometry + Line2
 * - Thick, anti-aliased lines with proper resolution handling
 * - Configurable outline color, opacity, and thickness
 * - Individual line segments prevent diagonal connection artifacts
 * - Compatible with existing ObjectSelector and SelectionManager
 * @category Experience
 * @subcategory ObjectManagement
 */
export default class SelectionHighlightManager {
  private experience: any;
  private scene: THREE.Scene;
  private eventHub: any;
  private highlightConfig: HighlightConfig;
  private highlightMeshes: Map<string, THREE.Group>;
  private highlightedObjects: Set<THREE.Object3D>;

  /**
   * Creates an instance of SelectionHighlightManager.
   * @param experience - The experience object containing scene, event hub, and other resources.
   */
  constructor(experience: any) {
    this.experience = experience;
    this.scene = experience.scene;
    this.eventHub = experience.eventHub;
    
    // Configuration for highlight appearance (matching VisualFeedbackManager)
    this.highlightConfig = {
      color: new THREE.Color(0xc6ee3d), // Lime green - same as bBox selection
      opacity: 1, // 35% transparency - same as bBox selection
      lineWidth: 5 // Line2 linewidth in pixels
    };
    
    // Storage for highlight meshes and selected objects
    this.highlightMeshes = new Map<string, THREE.Group>(); // key: objectUuid_childUuid -> highlightMesh
    this.highlightedObjects = new Set<THREE.Object3D>();
    
    // Initialize the highlight system
    this.setupEventListeners();
    
    log('SelectionHighlightManager initialized with Line2 approach');
  }

  /**
   * Sets up event listeners for selection-related events.
   */
  private setupEventListeners(): void {
    // Listen to selection events from SelectionManager
    this.eventHub.on("selectionUpdated", this.handleSelectionUpdated.bind(this));
    this.eventHub.on("selectedObjectsInquiryResponse", this.handleSelectionUpdated.bind(this));
    
    // Listen to resize events for Line2 resolution updates
    this.eventHub.on("resize", this.handleResize.bind(this));
    
    log("SelectionHighlightManager listeners set up");
  }

  /**
   * Handles selection update events and manages highlight meshes.
   * @param event - Selection event containing updated selected objects
   */
  private handleSelectionUpdated(event: SelectionEvent): void {
    if (!Array.isArray(event.selectedObjects)) {
      return;
    }

    const newSelection = new Set(event.selectedObjects);
    log("Selection updated, objects:", newSelection.size);

    // Remove highlights from objects no longer selected
    this.highlightedObjects.forEach(object => {
      if (!newSelection.has(object)) {
        this.removeHighlight(object);
      }
    });

    // Add highlights to newly selected objects
    newSelection.forEach(object => {
      if (!this.highlightedObjects.has(object)) {
        this.addHighlight(object);
      }
    });

    this.highlightedObjects = newSelection;
  }

  /**
   * Handles window resize events and updates Line2 material resolutions.
   * @param newSize - New window dimensions
   */
  private handleResize(newSize: { width: number; height: number }): void {
    // Update Line2 material resolution for all highlight meshes
    this.highlightMeshes.forEach(highlightMesh => {
      highlightMesh.children.forEach(child => {
        if (child instanceof Line2 && child.material instanceof LineMaterial) {
          child.material.resolution.set(newSize.width, newSize.height);
        }
      });
    });
    
    log("Line2 materials updated for new resolution:", newSize.width, "x", newSize.height);
  }

  /**
   * Adds highlight outlines to a selected object.
   * @param object - The object to add highlights to
   */
  private addHighlight(object: THREE.Object3D): void {
    if (!object || !object.children) return;

    log("Adding highlight to object:", object.name || object.uuid);

    // Find meshes to highlight within the object
    object.traverse((child) => {
      if (child instanceof THREE.Mesh && child.geometry && child.visible) {
        // Skip if this is already a highlight mesh
        if (child.userData.isHighlight) return;
        
        // Only highlight the main part geometry, skip text, bounding boxes, etc.
        const skipNames = ['bBox', 'partNumber', 'MfgPart', 'ring', 'circle'];
        if (skipNames.includes(child.name)) {
          return; // Skip these elements
        }
        
        // Only highlight if it's the main shape/part geometry
        if (child.name === 'Shape' || (!child.name && child.geometry)) {
          // Create highlight for this mesh
          const highlightMesh = this.createHighlightMesh(child);
          if (highlightMesh) {
            // Add to the same parent as the original mesh
            child.parent?.add(highlightMesh);
            
            // Store reference for cleanup
            const key = `${object.uuid}_${child.uuid}`;
            this.highlightMeshes.set(key, highlightMesh);
            
            log("Created highlight mesh for:", child.name || child.uuid);
          }
        }
      }
    });
  }

  /**
   * Removes highlight outlines from an object.
   * @param object - The object to remove highlights from
   */
  private removeHighlight(object: THREE.Object3D): void {
    if (!object) return;

    log("Removing highlight from object:", object.name || object.uuid);

    // Remove all highlight meshes for this object
    const keysToRemove: string[] = [];
    this.highlightMeshes.forEach((highlightMesh, key) => {
      if (key.startsWith(object.uuid)) {
        // Remove from scene
        if (highlightMesh.parent) {
          highlightMesh.parent.remove(highlightMesh);
        }
        
        // Clean up geometry and material (handle groups of Line2 objects)
        highlightMesh.children.forEach(child => {
          if (child.geometry) child.geometry.dispose();
          if (child.material) child.material.dispose();
        });
        
        keysToRemove.push(key);
      }
    });

    // Clean up references
    keysToRemove.forEach(key => {
      this.highlightMeshes.delete(key);
    });
  }

  /**
   * Creates a highlight mesh group with Line2 segments for the given original mesh.
   * @param originalMesh - The mesh to create highlights for
   * @returns The highlight mesh group or null if creation failed
   */
  private createHighlightMesh(originalMesh: THREE.Mesh): THREE.Group | null {
    if (!originalMesh.geometry) return null;

    try {
      // Create edge geometry and convert to individual Line2 segments
      const edges = new THREE.EdgesGeometry(originalMesh.geometry, 15);
      const positionArray = edges.attributes.position.array;
      
      // Create a group to hold all the individual line segments
      const highlightGroup = new THREE.Group();
      
      // Create individual Line2 objects for each edge (prevents diagonal connections)
      for (let i = 0; i < positionArray.length; i += 6) {
        // Create geometry for single line segment
        const segmentPositions = [
          positionArray[i], positionArray[i + 1], positionArray[i + 2],       // Point A
          positionArray[i + 3], positionArray[i + 4], positionArray[i + 5]     // Point B  
        ];
        
        const lineGeometry = new LineGeometry();
        lineGeometry.setPositions(segmentPositions);
        
        // Create Line2 material for this segment
        const lineMaterial = new LineMaterial({
          color: this.highlightConfig.color.getHex(),
          linewidth: this.highlightConfig.lineWidth,
          transparent: true,
          opacity: this.highlightConfig.opacity,
          depthTest: false,
          depthWrite: false
        });
        
        // Set resolution for Line2 material
        const width = this.experience.sizes?.reSize?.width || window.innerWidth;
        const height = this.experience.sizes?.reSize?.height || window.innerHeight;
        lineMaterial.resolution.set(width, height);
        
        // Create Line2 for this segment
        const segmentLine = new Line2(lineGeometry, lineMaterial);
        segmentLine.computeLineDistances();
        
        // Add to group
        highlightGroup.add(segmentLine);
      }
      
      log('Created', highlightGroup.children.length, 'Line2 segments with linewidth:', this.highlightConfig.lineWidth);
      
      // Copy transform from original mesh
      highlightGroup.position.copy(originalMesh.position);
      highlightGroup.rotation.copy(originalMesh.rotation);
      highlightGroup.scale.copy(originalMesh.scale);
      
      // Copy matrix transformations if any
      if (originalMesh.matrix) {
        highlightGroup.matrix.copy(originalMesh.matrix);
        highlightGroup.matrixAutoUpdate = originalMesh.matrixAutoUpdate;
      }
      
      // Ensure it renders on top
      highlightGroup.renderOrder = originalMesh.renderOrder + 1;
      highlightGroup.position.z += 0.001; // Slight Z offset to render on top
      
      // Tag it as a highlight mesh for identification
      highlightGroup.userData.isHighlight = true;
      highlightGroup.userData.originalMesh = originalMesh;
      
      // Make it not interfere with raycasting
      highlightGroup.raycast = () => {};
      
      return highlightGroup;
      
    } catch (error) {
      console.warn("Could not create highlight mesh:", error);
      return null;
    }
  }

  /**
   * Animation update method - call this in your render loop.
   */
  public update(): void {
    // Line2 materials don't need animation updates
    // This method is kept for compatibility with the render loop
  }

  /**
   * Render method for compatibility - no special rendering needed.
   */
  public render(): void {
    // Line2 renders automatically with the scene
    // This method is kept for compatibility but does nothing
  }

  // Configuration methods for real-time adjustments

  /**
   * Updates the highlight color for all existing highlights.
   * @param color - New color value (hex number or THREE.Color)
   */
  public setHighlightColor(color: number | THREE.Color): void {
    if (typeof color === 'number') {
      this.highlightConfig.color.set(color);
    } else {
      this.highlightConfig.color.copy(color);
    }
    
    // Update all existing highlight meshes
    this.highlightMeshes.forEach(highlightMesh => {
      highlightMesh.children.forEach(child => {
        if (child instanceof Line2 && child.material instanceof LineMaterial) {
          child.material.color.set(this.highlightConfig.color.getHex());
        }
      });
    });
    
    log("Highlight color updated to:", color);
  }

  /**
   * Updates the highlight opacity for all existing highlights.
   * @param opacity - New opacity value (0-1)
   */
  public setHighlightOpacity(opacity: number): void {
    this.highlightConfig.opacity = Math.max(0, Math.min(1, opacity)); // Clamp to 0-1
    
    // Update all existing highlight meshes
    this.highlightMeshes.forEach(highlightMesh => {
      highlightMesh.children.forEach(child => {
        if (child instanceof Line2 && child.material instanceof LineMaterial) {
          child.material.opacity = this.highlightConfig.opacity;
        }
      });
    });
    
    log("Highlight opacity updated to:", opacity);
  }

  /**
   * Updates the line width for all existing highlights.
   * @param width - New line width in pixels
   */
  public setLineWidth(width: number): void {
    this.highlightConfig.lineWidth = Math.max(1, width); // Minimum 1 pixel
    
    // Update all existing highlight meshes
    this.highlightMeshes.forEach(highlightMesh => {
      highlightMesh.children.forEach(child => {
        if (child instanceof Line2 && child.material instanceof LineMaterial) {
          child.material.linewidth = this.highlightConfig.lineWidth;
        }
      });
    });
    
    log("Line width updated to:", width);
  }

  /**
   * Gets the current highlight configuration.
   * @returns Current highlight configuration
   */
  public getHighlightConfig(): HighlightConfig {
    return { 
      color: this.highlightConfig.color.clone(),
      opacity: this.highlightConfig.opacity,
      lineWidth: this.highlightConfig.lineWidth
    };
  }

  /**
   * Updates multiple highlight settings at once.
   * @param config - Partial configuration object
   */
  public updateHighlightConfig(config: Partial<HighlightConfig>): void {
    if (config.color !== undefined) {
      this.setHighlightColor(config.color);
    }
    if (config.opacity !== undefined) {
      this.setHighlightOpacity(config.opacity);
    }
    if (config.lineWidth !== undefined) {
      this.setLineWidth(config.lineWidth);
    }
    
    log("Highlight configuration updated:", config);
  }

  /**
   * Clears highlight state when the scene is deleted (e.g. 2D/3D switch).
   * Prevents holding references to removed objects.
   */
  public clearForSceneDelete(): void {
    this.highlightMeshes.clear();
    this.highlightedObjects.clear();
  }

  /**
   * Cleanup method to dispose of resources and remove event listeners.
   */
  public dispose(): void {
    // Remove event listeners
    this.eventHub.off("selectionUpdated", this.handleSelectionUpdated.bind(this));
    this.eventHub.off("selectedObjectsInquiryResponse", this.handleSelectionUpdated.bind(this));
    this.eventHub.off("resize", this.handleResize.bind(this));
    
    // Clean up all highlight meshes
    this.highlightMeshes.forEach((highlightMesh, key) => {
      if (highlightMesh.parent) {
        highlightMesh.parent.remove(highlightMesh);
      }
      highlightMesh.children.forEach(child => {
        if (child.geometry) child.geometry.dispose();
        if (child.material) child.material.dispose();
      });
    });
    
    // Clear data structures
    this.highlightMeshes.clear();
    this.highlightedObjects.clear();
    
    log("SelectionHighlightManager disposed");
  }
}